import {FormLabel} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import moment from 'moment';
import MessageBox from '../../../../util/messageBox';
import uuidv4 from 'uuid/v4';
import config from '../../../../app/config';
import _ from 'lodash';
import {ClinicianIssuerCode, ClinicianTier} from '../../../../controls/agyle/dictionaries';
import API from '../../../../app/api';
import {useStylesECDS} from './ecdsStyle';
import {useSelector} from 'react-redux';
import {clinicianIssuerCode, cliniciansDict, clinicianTier} from '../../../../store/reducers/dicts';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import AgyleDatePicker from '../../../../components/ui/fields/agyleDatePicker';
import AgyleBlockSwitch from '../../../../components/ui/switch/agyleBlockSwitch';
import AgyleAccordion from '../../../../components/ui/accordion/agyleAccordion';
import {AgyleTable, createAgyleTableColumn} from '../../../../components/table/AgyleTable';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import useOpenState from '../../../../hooks/useOpenState';
import {calculateArrivalDate} from '../../../../util/patientUtil';
import AgyleLoadingSpinner from '../../../../components/other/agyleLoadingSpinner';

const COLUMNS = [
    createAgyleTableColumn('no', 'No.', 1, null, 'count', 'notSort', 'medium', 'center', true, false, null, null, null),
    createAgyleTableColumn('idEntry', 'Entry Id', 2, 'ProfessionalRegistrationEntryIdentifier', 'string', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('issuerCode', 'Registration Issuer', 3, 'ProfessionalRegistrationIssuerCode', 'dict', '', 'medium', 'left', true, true, null, null, null),
    createAgyleTableColumn('tier', 'Tier', 4, 'CareProfessionalTier_EmergencyCare', 'dict', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('dischargeResp', 'Responsible for Discharge', 5, 'CareProfessionalDischargeResponsibilityIndicator_EmergencyCare', 'string', '', 'medium', 'left', true, false, null, null, null),
    createAgyleTableColumn('timestamp', 'Date and Time', 6, 'timestamp', 'string', '', 'medium', 'left', true, true, null, null, null),
    createAgyleTableColumn('action', 'Action', 7, null, 'action', 'notSort', 'medium', 'left', true, true, null, null, null),
];

const CareProfessionals = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();

    const {isSmall} = props;

    const controller = new AbortController();

    // Multi Entry list
    const [list, setList] = useState([]);

    // Data
    const [dataForm, setDataForm] = useState({
        issuerCode: null,
        idEntry: '',
        tier: null,
        dischargeResp: 'N',
        timestamp: null,
    });
    const [picker, setPicker] = useState(null);

    // Validation
    const [error, setError] = useState({
        issuerCode: false,
        idEntry: false,
        tier: false,
        timestamp: false,
    });

    // invalid pickers
    const errorPicker = useRef(null);

    //  For Delete
    const [deleteItem, setDeleteItem] = useState(null);
    const [isConfirmRemoveElementOpen, openConfirmRemoveElement, closeConfirmRemoveElement] = useOpenState(false, true);

    // Loading state
    const [loading, setLoading] = useState(false);

    // Accordion
    const [expanded, setExpanded] = React.useState('care');

    // Dictionaries
    const clinicianTierDict = useSelector(clinicianTier);
    const clinicianIssuerCodeDict = useSelector(clinicianIssuerCode);
    const clinicianDict = useSelector(cliniciansDict);

    // Init data
    useEffect(() => {
        const userList = [];
        const auditObj = [];
        const usersMap = new Map();

        setLoading(true);

        // loading audit data
        initAuditData()
            .then(res => {
                auditObj.push(...res);
            })
            .finally(() => {
                getCliniciansFromCurrentState(auditObj);

                const sortList = _.orderBy(auditObj, element => moment(element.timestamp), ['desc']);

                // Filter duplicate and get first timestamp
                _.forEach(sortList, list => {
                    usersMap.set(list.login, list.timestamp);
                });

                for (let [key, value] of usersMap) {
                    const user = _.find(clinicianDict, {userName: key});
                    const isDischargeResponsibility = props.patient?.studies?.discharge?.who?.userName === key;

                    if (!_.isNil(user)) {
                        const tier = _.find(clinicianTierDict, {__code: checkLength(user?.tier)});
                        const issuer = _.find(clinicianIssuerCodeDict, {__code: checkLength(user?.issuerCode)});

                        userList.push({
                            id: uuidv4() + '',
                            ProfessionalRegistrationIssuerCode: issuer || null,
                            ProfessionalRegistrationEntryIdentifier: user?.__code ? user?.__code : '',
                            CareProfessionalTier_EmergencyCare: tier || null, // V3
                            CareProfessionalTier_UrgentAndEmergencyCare: tier || null, // V4
                            CareProfessionalDischargeResponsibilityIndicator_EmergencyCare: isDischargeResponsibility ? 'Y' : 'N',  // V3
                            CareProfessionalDischargeResponsibilityIndicator_UrgentAndEmergencyCare: isDischargeResponsibility ? 'Y' : 'N', // V4
                            CareProfessionalClinicalResponsibilityTimeStamp: value, // V3
                            CareProfessionalClinicalResponsibilityTimestamp: value, // V4
                            timestamp: moment.utc(value).local().format(config.defaultDateTimeFormat),
                        });
                    }
                }
                setList([...userList]);
                setLoading(false);
            });

        return () => {
            controller.abort();
        };
    }, []);

    // Helper method for return valid tier and issuer code
    // Must be with 0 at start
    const checkLength = (tier) => {
        if (!_.isNil(tier)) {
            return tier.lenght > 1 ? tier : '0' + tier;
        }
        return '';
    };

    // Get Clinicians from the current main screen
    const getCliniciansFromCurrentState = (auditObj) => {
        const clinicians = [];

        const getClinician = (path, userPath = 'who') => {
            const user = _.get(props.patient, path);
            if (user?.[userPath]) {
                clinicians.push({
                    login: user?.[userPath]?.userName,
                    timestamp: moment(user?.when),
                });
            }
        };

        const getClinicianFromArray = (path, userPath, timePath) => {
            const val = _.get(props.patient, path);
            if (!_.isEmpty(val)) {

                _.forEach(val, v => {
                    if (_.get(v, userPath) && _.get(v, timePath)) {
                        clinicians.push({
                            login: _.get(v, userPath)?.userName,
                            timestamp: moment(_.get(v, timePath)),
                        });
                    }
                });
            }
        };

        getClinician('ambulanceHandover');  // Ambulance Handover
        getClinician('clinicalConclusion'); // Clinical Conclusion
        getClinician('seen'); // Rapid Assessment
        getClinician('firstClinician'); // Clerking Clinician
        getClinician('seniorReview'); // Senior Review
        getClinician('handover'); // Handover
        getClinician('triage', 'triageClinicians'); // Triage
        getClinician('clerking', 'clinician'); // Clerking

        // For old data
        getClinician('triage'); // Triage
        getClinician('clerking'); // Clerking

        getClinicianFromArray('studies.prescription.list', 'values.who', 'values.when'); // Prescription form
        getClinicianFromArray('studies.medicalPlan.notes', 'createdBy', 'timestamp'); // Medical Plan
        getClinicianFromArray('studies.nursingPlanNote.notes', 'createdBy', 'timestamp'); // Nursing Plan
        getClinicianFromArray('studies.treatmentPlan.notes', 'createdBy', 'timestamp'); // Treatment PLan

        // CSM Contact Time
        if (props.patient?.csmContactedTime?.by) {
            clinicians.push({
                login: props.patient?.csmContactedTime?.by?.userName,
                timestamp: moment(props.patient?.csmContactedTime?.when),
            });
        }

        // Bed Request
        if (props.patient?.studies?.bedRequest) {
            if (props.patient?.studies?.bedRequest?.allocatedWho) {
                clinicians.push({
                    login: props.patient?.studies?.bedRequest?.allocatedWho?.userName,
                    timestamp: moment(props.patient?.studies.bedRequest.allocatedWhen),
                });
            }
            if (props.patient?.studies?.bedRequest?.readyWho) {
                clinicians.push({
                    login: props.patient?.studies?.bedRequest?.readyWho?.userName,
                    timestamp: moment(props.patient?.studies.bedRequest.readyWhen),
                });
            }
            if (props.patient?.studies?.bedRequest?.canceledWho) {
                clinicians.push({
                    login: props.patient?.studies?.bedRequest?.canceledWho?.userName,
                    timestamp: moment(props.patient?.studies.bedRequest.canceledWhen),
                });
            }
            if (props.patient?.studies?.bedRequest?.requestedWho) {
                clinicians.push({
                    login: props.patient?.studies?.bedRequest?.requestedWho?.userName,
                    timestamp: moment(props.patient?.studies.bedRequest.requestedWhen),
                });
            }
        }

        // Current Clinician
        if (props.patient?.currentClinician?.userName && props.patient?.currentClinician?.timestamp) {
            clinicians.push({
                login: props.patient?.currentClinician?.userName,
                timestamp: moment(props.patient?.currentClinician?.timestamp),
            });
        }

        // Discharge Clinician
        if (props.patient?.studies?.discharge?.who) {
            clinicians.push({
                login: props.patient?.studies?.discharge?.who?.userName,
                timestamp: moment(props.patient?.studies?.discharge?.date),
            });
        }

        // See and Treat
        if (props.patient?.studies?.seeAndTreat?.clinician) {
            clinicians.push({
                login: props.patient?.studies?.seeAndTreat?.clinician?.userName,
                timestamp: moment(props.patient?.studies?.seeAndTreat?.when),
            });
        }
        auditObj.push(...clinicians);
    };

    // Get all audit data for patients
    const initAuditData = async () => {
        let auditObj = [];

        // API params
        const options = {
            '$filter': `objectNew.patient_id eq '${props.patient.patient.__id}' ` + // for search changes in "patientsInWards"/"clinicalNotes"/"examinations"
                `or objectNew._id eq '${props.patient.patient.__id}' ` + // for search changes in "patients.data"
                `and createdDate ge_dt '${moment(props.patient?.admission?.startDate).format()}' ` + // since admitted date
                `and createdDate le_dt '${props.patient?.admission?.endDate
                    ? moment(props.patient?.admission?.endDate).format()
                    : moment().format()}'`, // to current time or discharge date
        };

        await API.getAuditsList(options, controller.signal).then(res => {
            if (res.status === 200 && !_.isEmpty(res?.data?.__data)) {
                _.forEach(res?.data?.__data, element => {

                    // Double check for date range, because sometimes filter in a query doesn't work.
                    if (moment(element.createdDate).isAfter(moment(props.patient?.admission?.startDate))
                        && moment(element.createdDate).isBefore(moment(props.patient?.admission?.endDate
                            ? moment(props.patient?.admission?.startDate).format(config.ecdsDateTimeFormat)
                            : moment()).format(config.ecdsDateTimeFormat))) {

                        // Filtering of the user who created the PiW
                        if (element?.objectName !== 'patientsInWards' || (element?.objectName === 'patientsInWards' && !_.isNil(element?.objectOld?.patient_id))) {
                            // get only user login and date
                            auditObj.push({
                                login: element?.createdBy,
                                timestamp: moment(element?.createdDate).utc().format(config.ecdsDateTimeFormat),
                            });
                        }
                    }
                });
            }
        });
        return auditObj;
    };

    const setDataDictionary = () => {
        let formattedList = _.cloneDeep(list);

        if (props.ecdsData) {
            for (const element of formattedList) {
                element.ProfessionalRegistrationIssuerCode = element.ProfessionalRegistrationIssuerCode ? element.ProfessionalRegistrationIssuerCode?.__code : '';
                element.CareProfessionalTier_EmergencyCare = element.CareProfessionalTier_EmergencyCare ? element.CareProfessionalTier_EmergencyCare?.__code : '';
                delete element.timestamp;
            }
        }

        const data = {CareProfessionals_EmergencyCare: formattedList};
        props.setData(data);
    };

    useEffect(() => {
        setDataDictionary();
    }, [list]);

    const addToList = () => {
        if (validation()) {
            const array = _.cloneDeep(list);
            const data = {
                id: dataForm.id === null ? uuidv4() + '' : dataForm.id,
                ProfessionalRegistrationIssuerCode: dataForm.issuerCode,
                ProfessionalRegistrationEntryIdentifier: dataForm.idEntry,
                CareProfessionalTier_EmergencyCare: dataForm.tier,  // V3
                CareProfessionalTier_UrgentAndEmergencyCare: dataForm.tier,  // V3
                CareProfessionalDischargeResponsibilityIndicator_EmergencyCare: dataForm.dischargeResp,  // V3
                CareProfessionalDischargeResponsibilityIndicator_UrgentAndEmergencyCare: dataForm.dischargeResp,  // V3
                CareProfessionalClinicalResponsibilityTimeStamp: picker ? moment(picker).utc().format(config.ecdsDateTimeFormat) : '',  // V3
                CareProfessionalClinicalResponsibilityTimestamp: picker ? moment(picker).utc().format(config.ecdsDateTimeFormat) : '', // V4
                timestamp: moment.utc(picker).local().format(config.defaultDateTimeFormat),
            };

            let index = _.findIndex(array, {id: data.id});
            if (index === -1) array.push(data);
            else array[index] = data;

            setList(array);
            clearData();
        }
    };

    const validation = () => {
        const errors = {
            issuerCode: dataForm.issuerCode === '',
            idEntry: dataForm.idEntry === '',
            tier: dataForm.tier === '',
            timestamp: errorPicker.current != null,
        };
        setError((prevState => ({...prevState, ...errors})));

        return !errors.timestamp && !errors.idEntry && !errors.tier && !errors.issuerCode;
    };

    const clearData = () => {
        const clearDataForm = {
            issuerCode: null,
            idEntry: '',
            tier: null,
            dischargeResp: 'N',
            timestamp: null,
        };

        setPicker(null);

        const clearError = {
            issuerCode: false,
            idEntry: false,
            tier: false,
            timestamp: false,
        };

        setDataForm(prevState => ({...prevState, ...clearDataForm}));
        setError((prevState => ({...prevState, ...clearError})));
    };

    const changeHandler = (event) => {
        const {name, value} = event.target;

        switch (name) {
            case 'ProfessionalRegistrationIssuerCode':
                setDataForm(prevState => ({...prevState, 'issuerCode': value}));
                setError((prevState => ({
                    ...prevState,
                    'issuerCode': !props.checkLenght(2, 2, value?.__code || null),
                })));
                break;
            case 'ProfessionalRegistrationEntryIdentifier':
                setDataForm(prevState => ({...prevState, 'idEntry': value}));
                setError((prevState => ({...prevState, 'idEntry': !props.checkLenght(1, 32, value)})));
                break;
            case 'CareProfessionalTier_EmergencyCare':
                setDataForm(prevState => ({...prevState, 'tier': value}));
                setError((prevState => ({...prevState, 'tier': !props.checkLenght(2, 2, value?.__code || null)})));
                break;
            default:
                break;
        }
    };

    const dictionaryChangeHandler = (data) => {
        changeHandler({
            target: {
                name: data[0],
                value: data?.[1] || null,
            },
        });
    };

    const dateHandler = (name, event) => {
        const date = moment(event);

        setPicker(date);
        setError((prevState => ({...prevState, 'timestamp': false})));
    };

    const checkboxHandler = (val) => {
        setDataForm(prevState => ({...prevState, 'dischargeResp': val ? 'Y' : 'N'}));
    };

    const handleAccordionChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    };

    const deleteEntry = () => {
        setList(prevState => prevState.filter(value => value.id !== deleteItem?.id));
        setDeleteItem(null);
        closeConfirmRemoveElement();
        clearData();
    };

    const editEntry = (entry) => {
        setError({
            issuerCode: false,
            idEntry: false,
            tier: false,
            timestamp: false,
        });

        setDataForm({
            id: entry.id,
            issuerCode: entry.ProfessionalRegistrationIssuerCode ? entry.ProfessionalRegistrationIssuerCode : null,
            idEntry: entry.ProfessionalRegistrationEntryIdentifier ? entry.ProfessionalRegistrationEntryIdentifier : '',
            tier: entry.CareProfessionalTier_EmergencyCare ? entry.CareProfessionalTier_EmergencyCare : null,
            dischargeResp: entry.CareProfessionalDischargeResponsibilityIndicator_EmergencyCare ? entry.CareProfessionalDischargeResponsibilityIndicator_EmergencyCare : 'N',
            timestamp: entry.CareProfessionalClinicalResponsibilityTimeStamp,
        });
        setPicker(entry.CareProfessionalClinicalResponsibilityTimeStamp ? moment.utc(entry.CareProfessionalClinicalResponsibilityTimeStamp).local() : null);
    };

    const removeEntry = (entry) => {
        setDeleteItem(entry);
        openConfirmRemoveElement();
    };

    const renderTable = () => {
        if (!_.isEmpty(list) && !loading) {
            return (
                <div style={{marginBottom: 15}}>
                    <AgyleAccordion
                        expanded={expanded === 'care'}
                        onChange={handleAccordionChange('care')}
                        label={t('Table')}
                    >
                        <AgyleTable
                            columns={COLUMNS}
                            data={list}
                            editItem={editEntry}
                            deleteItem={removeEntry}
                            disableFilter
                            edRefresh
                        />
                    </AgyleAccordion>
                </div>
            );
        }
    };

    if (props.accordion !== 'Care Professionals') return null;

    return (
        <div className={classes.root}>
            {loading && <AgyleLoadingSpinner/>}

            {!_.isEmpty(list) && renderTable()}

            <Grid container spacing={2} direction="row">
                <Grid item xs={isSmall ? 12 : 6}>
                    <ClinicianIssuerCode
                        name="ProfessionalRegistrationIssuerCode"
                        label={t('Professional Registration Issuer')}
                        color="primary"
                        value={dataForm.issuerCode}
                        onChange={(_e, val) => dictionaryChangeHandler(['ProfessionalRegistrationIssuerCode', val])}
                        error={error.issuerCode}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <ClinicianTier
                        name="CareProfessionalTier_EmergencyCare"
                        label={t('Care Professional Tier')}
                        color="primary"
                        value={dataForm.tier}
                        onChange={(_e, val) => dictionaryChangeHandler(['CareProfessionalTier_EmergencyCare', val])}
                        error={error.tier}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="ProfessionalRegistrationEntryIdentifier"
                        label={t('Professional Registration Entry Identifier')}
                        onChange={changeHandler}
                        value={dataForm.idEntry || ''}
                        error={error.idEntry}
                        helperText={error.idEntry ? 'Input must be max 32 characters long.' : ''}
                        FormHelperTextProps={{className: classes.helperText}}
                        maxLength={32}
                        minLength={1}
                        fullWidth
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleDatePicker
                        inputFormat={config.defaultDateTimeFormat}
                        name="CareProfessionalClinicalResponsibilityTimeStamp"
                        label={t('Clinical Responsibility Date and Time')}
                        value={picker || null}
                        onChange={(e) => dateHandler('CareProfessionalClinicalResponsibilityTimeStamp', e)}
                        error={error.timestamp}
                        type="dateTime"
                        minDate={moment(calculateArrivalDate(props.patient))}
                        maxDatee={moment()}
                        onError={event => errorPicker.current = event}
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12} className={classes.divRow}>
                    <div className={classes.divRow}>
                        <AgyleBlockSwitch
                            onChange={checkboxHandler}
                            value={dataForm.dischargeResp === 'Y'}
                            style={{marginRight: 10}}
                        />

                        <FormLabel className={classes.label}>
                            {t('Responsible for discharge of the patient?')}
                        </FormLabel>
                    </div>

                    {!isSmall &&
                        <AgyleButton
                            variant="contained"
                            onClick={addToList}
                            style={{marginLeft: 'auto'}}
                        >
                            {t('Add Record')}
                        </AgyleButton>
                    }
                </Grid>

                {isSmall &&
                    <Grid item xs={12}>
                        <AgyleButton
                            variant="contained"
                            onClick={addToList}
                            style={{marginLeft: 'auto'}}
                        >
                            {t('Add Record')}
                        </AgyleButton>
                    </Grid>
                }

            </Grid>

            <MessageBox
                open={isConfirmRemoveElementOpen}
                onClose={closeConfirmRemoveElement}
                title={t('Confirmation')}
                message={t('Do you really want to remove this item?')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={deleteEntry}
                warning
            />
        </div>
    );
};

export default CareProfessionals;
